/**
 * SmartPaw Cabinet — Pet Care Calendar page.
 *
 * Features:
 *  • Month grid + list view toggle
 *  • Per-pet & per-category filters
 *  • Add / edit / delete events
 *  • Mark single occurrences complete
 *  • iCal (.ics) export for Google / Apple / Outlook sync
 *  • Reminder configuration (email + WhatsApp/SMS — WhatsApp/SMS are
 *    delivered through the backend dispatcher once provider creds are set;
 *    until then they log a stub send).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
  Filter,
  Download,
  Trash2,
  Check,
  X,
  Loader2,
  ListChecks,
  CalendarRange,
  Pencil,
  Bell,
  Sparkles,
  Stethoscope,
  Phone,
  MapPin,
  Paperclip,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/cabinet/i18n";
import { api, API_BASE, formatApiError } from "@/cabinet/lib/api";
import { useAuth } from "@/cabinet/context/AuthContext";
import PageHeader from "@/cabinet/components/layout/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// --- date helpers -----------------------------------------------------------
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}
function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}
function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function fmtDate(d, lang) {
  return d.toLocaleDateString(lang === "ka" ? "ka-GE" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
function fmtTime(d, lang) {
  return d.toLocaleTimeString(lang === "ka" ? "ka-GE" : "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}
function toLocalInputValue(date) {
  // For <input type="datetime-local"> — yyyy-MM-ddThh:mm in LOCAL time.
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
function fromLocalInputValue(s) {
  // Treat input as local time and produce ISO with timezone offset.
  if (!s) return null;
  return new Date(s).toISOString();
}

const CATEGORY_COLORS = {
  medication: "bg-rose-100 text-rose-800 border-rose-200",
  vaccination: "bg-violet-100 text-violet-800 border-violet-200",
  grooming: "bg-sky-100 text-sky-800 border-sky-200",
  vet_visit: "bg-red-100 text-red-800 border-red-200",
  food_delivery: "bg-amber-100 text-amber-800 border-amber-200",
  exercise: "bg-emerald-100 text-emerald-800 border-emerald-200",
  custom: "bg-slate-100 text-slate-800 border-slate-200",
};

// ---------------------------------------------------------------------------
export default function CalendarPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [pets, setPets] = useState([]);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [view, setView] = useState("month"); // month | list
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [petFilter, setPetFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // create | edit
  const [draft, setDraft] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [downloading, setDownloading] = useState(false);
  const [stats, setStats] = useState(null);

  // Template picker state
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [tplAnchor, setTplAnchor] = useState(""); // yyyy-mm-dd
  const [tplPetId, setTplPetId] = useState("");
  const [applyingTpl, setApplyingTpl] = useState(false);

  // Bilingual category label resolver
  const catLabel = useCallback(
    (value) => {
      if (!meta) return value;
      const entry = meta.categories.find((c) => c.value === value);
      if (!entry) return value;
      return `${entry.emoji} ${lang === "ka" ? entry.label_ka : entry.label_en}`;
    },
    [meta, lang],
  );

  // -------- data loaders --------
  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get("/cabinet/calendar/meta");
      setMeta(data);
    } catch (e) {
      // Non-fatal: page can still render with defaults
    }
  }, []);

  const loadPets = useCallback(async () => {
    try {
      const { data } = await api.get("/pets");
      setPets(Array.isArray(data) ? data : data?.pets || []);
    } catch {
      setPets([]);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      // Month-view window must include the trailing days from the next month
      // that are visible on the grid (up to 6 weeks total = 42 days).
      const monthFirst = startOfMonth(viewDate);
      const weekday = (monthFirst.getDay() + 6) % 7; // Monday-start grid
      const gridFrom = addDays(monthFirst, -weekday);
      const gridTo = addDays(gridFrom, 42);
      // List view shows the next 60 days from today.
      const fromDt = view === "list" ? new Date() : gridFrom;
      const toDt = view === "list" ? addDays(new Date(), 60) : gridTo;
      const params = {
        from: fromDt.toISOString(),
        to: toDt.toISOString(),
      };
      if (petFilter !== "all") params.pet_id = petFilter;
      if (catFilter !== "all") params.category = catFilter;
      const [evRes, statsRes] = await Promise.all([
        api.get("/cabinet/calendar/events", { params }),
        api.get("/cabinet/calendar/stats").catch(() => ({ data: null })),
      ]);
      setEvents(evRes.data?.events || []);
      setStats(statsRes.data || null);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load calendar"));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [viewDate, view, petFilter, catFilter]);

  useEffect(() => {
    loadMeta();
    loadPets();
  }, [loadMeta, loadPets]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // -------- handlers --------
  const openCreate = (presetDate) => {
    const base = presetDate || new Date();
    base.setHours(9, 0, 0, 0);
    setDraft({
      title: "",
      category: "custom",
      pet_id: pets[0]?.pet_id || "",
      start_at: toLocalInputValue(base),
      duration_min: 30,
      notes: "",
      recurrence: "none",
      recurrence_until: "",
      reminder_offset: "24h",
      reminder_channels: ["email"],
    });
    setDialogMode("create");
    setDialogOpen(true);
  };

  const openEdit = (occ) => {
    if (occ.is_read_only) {
      toast.info(
        t("calendar.read_only_event") ||
          "This event is auto-generated from a subscription and can't be edited here.",
      );
      return;
    }
    setDraft({
      id: occ.id,
      title: occ.title || "",
      category: occ.category || "custom",
      pet_id: occ.pet_id || "",
      start_at: toLocalInputValue(new Date(occ.start_at)),
      duration_min: occ.duration_min || 30,
      notes: occ.notes || "",
      recurrence: occ.recurrence || "none",
      recurrence_until: occ.recurrence_until
        ? toLocalInputValue(new Date(occ.recurrence_until))
        : "",
      reminder_offset: occ.reminder_offset || "24h",
      reminder_channels: occ.reminder_channels || ["email"],
      attachments: occ.attachments || [],
    });
    setDialogMode("edit");
    setDialogOpen(true);
  };

  // ----- Attachments upload -----
  const handleUploadAttachment = async (file) => {
    if (!draft?.id || !file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: up } = await api.post("/cabinet/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: att } = await api.post(
        `/cabinet/calendar/events/${draft.id}/attachments`,
        {
          url: up.url,
          filename: up.filename,
          content_type: up.content_type,
          size: up.size,
        },
      );
      setDraft((d) => ({ ...d, attachments: [...(d.attachments || []), att] }));
      toast.success(t("calendar.attach_added") || "File attached");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleRemoveAttachment = async (attId) => {
    if (!draft?.id) return;
    try {
      await api.delete(
        `/cabinet/calendar/events/${draft.id}/attachments/${attId}`,
      );
      setDraft((d) => ({
        ...d,
        attachments: (d.attachments || []).filter((a) => a.id !== attId),
      }));
      toast.success(t("calendar.attach_removed") || "Attachment removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Resolve the selected draft's pet's vet (if any) — used to surface vet
  // contact whenever the event category is "vet_visit".
  const draftPetVet = React.useMemo(() => {
    if (!draft || draft.category !== "vet_visit") return null;
    if (!draft.pet_id) return null;
    const p = pets.find((x) => x.pet_id === draft.pet_id);
    return (p && p.vet) || null;
  }, [draft, pets]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!draft?.title?.trim()) {
      toast.error(t("calendar.title_required") || "Title is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: draft.title.trim(),
        category: draft.category,
        pet_id: draft.pet_id || null,
        start_at: fromLocalInputValue(draft.start_at),
        duration_min: Number(draft.duration_min) || 30,
        notes: draft.notes || null,
        recurrence: draft.recurrence,
        recurrence_until: draft.recurrence_until
          ? fromLocalInputValue(draft.recurrence_until)
          : null,
        reminder_offset: draft.reminder_offset,
        reminder_channels:
          draft.reminder_channels && draft.reminder_channels.length > 0
            ? draft.reminder_channels
            : ["email"],
      };
      if (dialogMode === "edit" && draft.id) {
        await api.patch(`/cabinet/calendar/events/${draft.id}`, payload);
        toast.success(t("calendar.event_updated") || "Event updated");
      } else {
        await api.post("/cabinet/calendar/events", payload);
        toast.success(t("calendar.event_created") || "Event added");
      }
      setDialogOpen(false);
      setDraft(null);
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!draft?.id) return;
    if (!window.confirm(t("calendar.confirm_delete") || "Delete this event?")) return;
    setSubmitting(true);
    try {
      await api.delete(`/cabinet/calendar/events/${draft.id}`);
      toast.success(t("calendar.event_deleted") || "Event deleted");
      setDialogOpen(false);
      setDraft(null);
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async (occ) => {
    if (occ.is_read_only) return;
    try {
      await api.post(`/cabinet/calendar/events/${occ.id}/complete`, {
        occurrence_at: occ.start_at,
      });
      toast.success(t("calendar.marked_done") || "Marked done");
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleUncomplete = async (occ) => {
    try {
      await api.post(`/cabinet/calendar/events/${occ.id}/uncomplete`, {
        occurrence_at: occ.start_at,
      });
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadIcs = async () => {
    setDownloading(true);
    try {
      // We use fetch with credentials so the auth cookie is sent.
      const res = await fetch(`${API_BASE}/cabinet/calendar/export.ics`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smartpaw-pet-calendar-${new Date()
        .toISOString()
        .slice(0, 10)}.ics`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("calendar.ics_downloaded") || "Calendar file downloaded");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to download .ics"));
    } finally {
      setDownloading(false);
    }
  };

  const openTemplates = async () => {
    setTemplatesOpen(true);
    setSelectedTemplate(null);
    if (!templatesLoaded) {
      try {
        const { data } = await api.get("/cabinet/calendar/templates");
        setTemplates(data?.templates || []);
        setTemplatesLoaded(true);
      } catch (e) {
        toast.error(formatApiError(e, "Failed to load templates"));
      }
    }
  };

  const selectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    // Default anchor:
    //  - birth_date templates → empty (force user to provide)
    //  - start_date templates → today
    if (tpl.anchor === "start_date") {
      const today = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      setTplAnchor(
        `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
      );
    } else {
      setTplAnchor("");
    }
    // Default pet to one matching the template species
    const matchingPet = pets.find(
      (p) =>
        tpl.species === "both" ||
        !p.species ||
        p.species.toLowerCase() === tpl.species,
    );
    setTplPetId(matchingPet?.pet_id || "");
  };

  const applyTemplate = async () => {
    if (!selectedTemplate) return;
    if (!tplAnchor) {
      toast.error(t("calendar.tpl_anchor_required") || "Please pick a date");
      return;
    }
    setApplyingTpl(true);
    try {
      const { data } = await api.post(
        `/cabinet/calendar/templates/${selectedTemplate.id}/apply`,
        {
          anchor_date: new Date(`${tplAnchor}T00:00:00`).toISOString(),
          pet_id: tplPetId || null,
          language: lang,
          reminder_offset: "24h",
          reminder_channels: ["email"],
        },
      );
      toast.success(
        (t("calendar.tpl_created") || "Added {count} events to your calendar").replace(
          "{count}",
          data?.created ?? 0,
        ),
      );
      setTemplatesOpen(false);
      setSelectedTemplate(null);
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApplyingTpl(false);
    }
  };

  // ------- month grid -------
  const monthCells = useMemo(() => {
    const first = startOfMonth(viewDate);
    const start = new Date(first);
    // Monday-start grid (matches GE/EU calendars)
    const weekday = (first.getDay() + 6) % 7; // 0 = Mon
    start.setDate(start.getDate() - weekday);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const day = addDays(start, i);
      const dayEvents = events.filter((e) =>
        isSameDay(new Date(e.start_at), day),
      );
      cells.push({ day, events: dayEvents });
    }
    return cells;
  }, [viewDate, events]);

  const monthLabel = viewDate.toLocaleDateString(
    lang === "ka" ? "ka-GE" : "en-GB",
    { month: "long", year: "numeric" },
  );

  const weekdayShort = useMemo(() => {
    const ref = new Date(2024, 0, 1); // Monday
    return Array.from({ length: 7 }).map((_, i) =>
      addDays(ref, i).toLocaleDateString(lang === "ka" ? "ka-GE" : "en-GB", {
        weekday: "short",
      }),
    );
  }, [lang]);

  // ---------------------------------------------------------------------------
  return (
    <div data-testid="calendar-page">
      <PageHeader
        title={t("calendar.title") || "Pet Care Calendar"}
        subtitle={
          t("calendar.subtitle") ||
          "Plan medications, grooming, vet visits, exercise and more — all in one place."
        }
        icon={<CalendarDays className="h-6 w-6 text-[hsl(var(--terracotta))]" />}
      />

      {/* Stats strip: streak / completion */}
      {stats && (stats.total_due > 0 || stats.streak > 0) && (
        <div
          className="mt-4 grid grid-cols-3 gap-2 sm:gap-3"
          data-testid="calendar-stats-strip"
        >
          <div className="rounded-xl border border-[hsl(var(--border))] bg-paper px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">
              {t("calendar.stat_streak") || "On-time streak"}
            </div>
            <div className="mt-1 flex items-baseline gap-1.5">
              <span className="text-2xl font-bold text-[hsl(var(--terracotta))]">
                {stats.streak}
              </span>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">
                {stats.streak === 1 ? "🔥" : stats.streak >= 5 ? "🔥🔥" : ""}
              </span>
            </div>
          </div>
          <div className="rounded-xl border border-[hsl(var(--border))] bg-paper px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">
              {t("calendar.stat_done") || "Tasks done"}
            </div>
            <div className="mt-1 text-2xl font-bold text-[hsl(var(--foreground))]">
              {stats.total_completed}
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                {" "}
                / {stats.total_due}
              </span>
            </div>
          </div>
          <div className="rounded-xl border border-[hsl(var(--border))] bg-paper px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">
              {t("calendar.stat_rate") || "Completion rate"}
            </div>
            <div className="mt-1 text-2xl font-bold text-[hsl(var(--foreground))]">
              {Math.round((stats.completion_rate || 0) * 100)}
              <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
                %
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-[hsl(var(--border))] bg-paper p-1">
          <button
            type="button"
            onClick={() => setView("month")}
            data-testid="calendar-view-month"
            className={
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition " +
              (view === "month"
                ? "bg-[hsl(var(--terracotta))] text-white"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]")
            }
          >
            <CalendarRange className="h-3.5 w-3.5" /> {t("calendar.view_month") || "Month"}
          </button>
          <button
            type="button"
            onClick={() => setView("list")}
            data-testid="calendar-view-list"
            className={
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition " +
              (view === "list"
                ? "bg-[hsl(var(--terracotta))] text-white"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]")
            }
          >
            <ListChecks className="h-3.5 w-3.5" /> {t("calendar.view_list") || "Upcoming"}
          </button>
        </div>

        {view === "month" && (
          <div className="inline-flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))
              }
              data-testid="calendar-prev-month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-2 text-sm font-bold text-[hsl(var(--foreground))] capitalize min-w-[140px] text-center">
              {monthLabel}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))
              }
              data-testid="calendar-next-month"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setViewDate(new Date())}
              data-testid="calendar-today"
              className="text-xs"
            >
              {t("calendar.today") || "Today"}
            </Button>
          </div>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Pet filter */}
          <Select value={petFilter} onValueChange={setPetFilter}>
            <SelectTrigger
              className="h-9 w-auto min-w-[140px] rounded-full bg-paper"
              data-testid="calendar-pet-filter"
            >
              <Filter className="mr-1.5 h-3.5 w-3.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("calendar.all_pets") || "All pets"}
              </SelectItem>
              {pets.map((p) => (
                <SelectItem key={p.pet_id} value={p.pet_id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Category filter */}
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger
              className="h-9 w-auto min-w-[140px] rounded-full bg-paper"
              data-testid="calendar-cat-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("calendar.all_categories") || "All categories"}
              </SelectItem>
              {(meta?.categories || []).map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.emoji} {lang === "ka" ? c.label_ka : c.label_en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            size="sm"
            variant="outline"
            onClick={openTemplates}
            data-testid="calendar-use-template"
            className="gap-1.5 border-[hsl(var(--terracotta))]/40 text-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/10"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {t("calendar.use_template") || "Use template"}
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={downloadIcs}
            disabled={downloading}
            data-testid="calendar-export-ics"
            className="gap-1.5"
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            {t("calendar.export_ics") || ".ics"}
          </Button>

          <Button
            size="sm"
            onClick={() => openCreate()}
            data-testid="calendar-add-event"
            className="gap-1.5 bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-white"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("calendar.add_event") || "Add event"}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="mt-5">
        {loading ? (
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 21 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : view === "month" ? (
          <MonthGrid
            cells={monthCells}
            weekdayShort={weekdayShort}
            viewDate={viewDate}
            onPickDay={openCreate}
            onClickEvent={openEdit}
            onToggleComplete={(occ) =>
              occ.is_completed ? handleUncomplete(occ) : handleComplete(occ)
            }
            t={t}
            lang={lang}
          />
        ) : (
          <UpcomingList
            events={events}
            onClickEvent={openEdit}
            onToggleComplete={(occ) =>
              occ.is_completed ? handleUncomplete(occ) : handleComplete(occ)
            }
            onAddEvent={() => openCreate()}
            t={t}
            lang={lang}
            catLabel={catLabel}
          />
        )}
      </div>

      {/* Add/edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-paper max-h-[85vh] overflow-y-auto" data-testid="calendar-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {dialogMode === "edit"
                ? t("calendar.edit_event") || "Edit event"
                : t("calendar.new_event") || "New event"}
            </DialogTitle>
          </DialogHeader>

          {draft && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="cal-title">{t("calendar.field_title") || "Title"} *</Label>
                <Input
                  id="cal-title"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder={t("calendar.title_placeholder") || "e.g. Heartworm pill"}
                  required
                  data-testid="event-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("calendar.field_category") || "Category"}</Label>
                  <Select
                    value={draft.category}
                    onValueChange={(v) => setDraft({ ...draft, category: v })}
                  >
                    <SelectTrigger data-testid="event-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(meta?.categories || []).map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.emoji} {lang === "ka" ? c.label_ka : c.label_en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("calendar.field_pet") || "Pet"}</Label>
                  <Select
                    value={draft.pet_id || "__none__"}
                    onValueChange={(v) =>
                      setDraft({ ...draft, pet_id: v === "__none__" ? "" : v })
                    }
                  >
                    <SelectTrigger data-testid="event-pet-select">
                      <SelectValue placeholder={t("calendar.no_pet") || "No specific pet"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        {t("calendar.no_pet") || "No specific pet"}
                      </SelectItem>
                      {pets.map((p) => (
                        <SelectItem key={p.pet_id} value={p.pet_id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cal-start">{t("calendar.field_when") || "When"} *</Label>
                  <Input
                    id="cal-start"
                    type="datetime-local"
                    value={draft.start_at}
                    onChange={(e) => setDraft({ ...draft, start_at: e.target.value })}
                    required
                    data-testid="event-start-input"
                  />
                </div>
                <div>
                  <Label htmlFor="cal-dur">
                    {t("calendar.field_duration") || "Duration (min)"}
                  </Label>
                  <Input
                    id="cal-dur"
                    type="number"
                    min="0"
                    max="1440"
                    value={draft.duration_min}
                    onChange={(e) =>
                      setDraft({ ...draft, duration_min: e.target.value })
                    }
                    data-testid="event-duration-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("calendar.field_repeat") || "Repeat"}</Label>
                  <Select
                    value={draft.recurrence}
                    onValueChange={(v) => setDraft({ ...draft, recurrence: v })}
                  >
                    <SelectTrigger data-testid="event-recurrence-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        {t("calendar.repeat_none") || "Does not repeat"}
                      </SelectItem>
                      <SelectItem value="daily">
                        {t("calendar.repeat_daily") || "Daily"}
                      </SelectItem>
                      <SelectItem value="weekly">
                        {t("calendar.repeat_weekly") || "Weekly"}
                      </SelectItem>
                      <SelectItem value="monthly">
                        {t("calendar.repeat_monthly") || "Monthly"}
                      </SelectItem>
                      <SelectItem value="yearly">
                        {t("calendar.repeat_yearly") || "Yearly"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {draft.recurrence !== "none" && (
                  <div>
                    <Label htmlFor="cal-until">
                      {t("calendar.field_until") || "Repeat until (optional)"}
                    </Label>
                    <Input
                      id="cal-until"
                      type="datetime-local"
                      value={draft.recurrence_until}
                      onChange={(e) =>
                        setDraft({ ...draft, recurrence_until: e.target.value })
                      }
                      data-testid="event-until-input"
                    />
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="cal-notes">{t("calendar.field_notes") || "Notes"}</Label>
                <Textarea
                  id="cal-notes"
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  rows={3}
                  placeholder={t("calendar.notes_placeholder") || "Dosage, vet name, etc."}
                  data-testid="event-notes-input"
                />
              </div>

              <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3">
                <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  <Bell className="h-3.5 w-3.5" />
                  {t("calendar.reminders") || "Reminders"}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">
                      {t("calendar.field_remind_when") || "Send"}
                    </Label>

              {/* Vet contact (only when category === vet_visit & pet has vet info) */}
              {draftPetVet && (
                <div className="rounded-lg border border-[hsl(var(--terracotta))]/30 bg-[hsl(var(--terracotta))]/5 p-3" data-testid="event-vet-card">
                  <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[hsl(var(--terracotta))]">
                    <Stethoscope className="h-3.5 w-3.5" />
                    {t("calendar.vet_contact") || "Vet contact"}
                  </div>
                  <div className="text-sm text-[hsl(var(--foreground))]">
                    {draftPetVet.name && (
                      <div className="font-bold">{draftPetVet.name}</div>
                    )}
                    {draftPetVet.clinic && (
                      <div className="text-[hsl(var(--muted-foreground))]">{draftPetVet.clinic}</div>
                    )}
                    {draftPetVet.phone && (
                      <a
                        href={`tel:${draftPetVet.phone}`}
                        className="inline-flex items-center gap-1 text-[hsl(var(--terracotta))] font-semibold mt-1 hover:underline"
                      >
                        <Phone className="h-3 w-3" /> {draftPetVet.phone}
                      </a>
                    )}
                    {draftPetVet.address && (
                      <div className="flex items-start gap-1 mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                        <MapPin className="h-3 w-3 mt-0.5 shrink-0" /> {draftPetVet.address}
                      </div>
                    )}
                    {draftPetVet.notes && (
                      <p className="mt-1 text-xs italic text-[hsl(var(--muted-foreground))]">
                        {draftPetVet.notes}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Attachments (only when editing an existing event) */}
              {dialogMode === "edit" && draft?.id && (
                <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-3">
                  <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    <Paperclip className="h-3.5 w-3.5" />
                    {t("calendar.attachments") || "Attachments"}
                  </div>
                  {(draft.attachments || []).length > 0 && (
                    <ul className="mb-2 space-y-1">
                      {draft.attachments.map((a) => (
                        <li
                          key={a.id}
                          className="flex items-center gap-2 rounded-md bg-paper px-2 py-1.5 text-xs"
                        >
                          <Paperclip className="h-3 w-3 text-[hsl(var(--muted-foreground))]" />
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 truncate text-[hsl(var(--primary))] hover:underline"
                          >
                            {a.filename || "attachment"}
                          </a>
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(a.id)}
                            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"
                            data-testid={`attach-remove-${a.id}`}
                            aria-label="Remove attachment"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-dashed border-[hsl(var(--border))] bg-paper px-3 py-1.5 text-xs font-bold text-[hsl(var(--foreground))] hover:border-[hsl(var(--terracotta))]/40 hover:bg-[hsl(var(--terracotta))]/5 transition">
                    <Paperclip className="h-3 w-3" />
                    {t("calendar.attach_upload") || "Attach receipt / photo"}
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      hidden
                      data-testid="event-attach-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUploadAttachment(f);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <p className="mt-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                    {t("calendar.attach_hint") ||
                      "Images or PDF receipts, up to 6 MB."}
                  </p>
                </div>
              )}


                    <Select
                      value={draft.reminder_offset}
                      onValueChange={(v) => setDraft({ ...draft, reminder_offset: v })}
                    >
                      <SelectTrigger data-testid="event-reminder-offset">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          {t("calendar.remind_none") || "No reminder"}
                        </SelectItem>
                        <SelectItem value="1h">
                          {t("calendar.remind_1h") || "1 hour before"}
                        </SelectItem>
                        <SelectItem value="24h">
                          {t("calendar.remind_24h") || "24 hours before"}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col justify-end gap-1.5 text-sm">
                    <ChannelToggle
                      label={t("calendar.via_email") || "Email"}
                      checked={draft.reminder_channels.includes("email")}
                      onChange={(c) =>
                        setDraft({
                          ...draft,
                          reminder_channels: c
                            ? Array.from(new Set([...draft.reminder_channels, "email"]))
                            : draft.reminder_channels.filter((x) => x !== "email"),
                        })
                      }
                      testId="chan-email"
                    />
                    <ChannelToggle
                      label={t("calendar.via_whatsapp") || "WhatsApp"}
                      checked={draft.reminder_channels.includes("whatsapp")}
                      onChange={(c) =>
                        setDraft({
                          ...draft,
                          reminder_channels: c
                            ? Array.from(new Set([...draft.reminder_channels, "whatsapp"]))
                            : draft.reminder_channels.filter((x) => x !== "whatsapp"),
                        })
                      }
                      testId="chan-whatsapp"
                      hint={t("calendar.whatsapp_pending") || "Coming soon"}
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 sm:gap-2">
                {dialogMode === "edit" && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={submitting}
                    data-testid="event-delete-btn"
                    className="mr-auto"
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" />
                    {t("calendar.delete") || "Delete"}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  disabled={submitting}
                >
                  {t("calendar.cancel") || "Cancel"}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="event-save-btn"
                  className="bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-white"
                >
                  {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {dialogMode === "edit"
                    ? t("calendar.save") || "Save changes"
                    : t("calendar.create") || "Add event"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      {/* Templates dialog */}
      <Dialog
        open={templatesOpen}
        onOpenChange={(o) => {
          if (!o) {
            setTemplatesOpen(false);
            setSelectedTemplate(null);
          }
        }}
      >
        <DialogContent
          className="max-w-2xl bg-paper max-h-[85vh] overflow-y-auto"
          data-testid="calendar-templates-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[hsl(var(--terracotta))]" />
              {selectedTemplate
                ? selectedTemplate[`title_${lang}`] || selectedTemplate.title_en
                : t("calendar.tpl_picker_title") || "Pre-built schedules"}
            </DialogTitle>
            {!selectedTemplate && (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {t("calendar.tpl_picker_subtitle") ||
                  "Set up a complete care schedule in one click — based on WSAVA/AAHA international standards."}
              </p>
            )}
          </DialogHeader>

          {!selectedTemplate ? (
            <ul className="grid gap-2 mt-2">
              {templates.length === 0 ? (
                <li className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--muted-foreground))]" />
                </li>
              ) : (
                templates.map((tpl) => (
                  <li key={tpl.id}>
                    <button
                      type="button"
                      onClick={() => selectTemplate(tpl)}
                      data-testid={`tpl-card-${tpl.id}`}
                      className="w-full text-left flex items-start gap-3 rounded-xl border border-[hsl(var(--border))] bg-paper p-4 hover:border-[hsl(var(--terracotta))]/40 hover:shadow-sm transition"
                    >
                      <span className="text-3xl shrink-0">{tpl.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h4 className="font-display font-bold text-[hsl(var(--foreground))]">
                            {tpl[`title_${lang}`] || tpl.title_en}
                          </h4>
                          <Badge variant="secondary" className="text-[10px]">
                            {tpl.event_count}{" "}
                            {t("calendar.tpl_events_count") || "events"}
                          </Badge>
                          {tpl.species !== "both" && (
                            <Badge variant="outline" className="text-[10px] capitalize">
                              {tpl.species === "dog"
                                ? t("calendar.tpl_for_dog") || "Dogs"
                                : t("calendar.tpl_for_cat") || "Cats"}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
                          {tpl[`description_${lang}`] || tpl.description_en}
                        </p>
                      </div>
                      <ChevronRightIcon className="h-4 w-4 text-[hsl(var(--muted-foreground))] mt-1 shrink-0" />
                    </button>
                  </li>
                ))
              )}
            </ul>
          ) : (
            <div className="space-y-4 mt-2">
              <div className="rounded-xl border border-[hsl(var(--terracotta))]/30 bg-[hsl(var(--terracotta))]/5 p-3 text-sm text-[hsl(var(--foreground))]">
                {selectedTemplate[`description_${lang}`] ||
                  selectedTemplate.description_en}
              </div>

              <div>
                <Label>
                  {selectedTemplate[`anchor_label_${lang}`] ||
                    selectedTemplate.anchor_label_en}{" "}
                  *
                </Label>
                <Input
                  type="date"
                  value={tplAnchor}
                  onChange={(e) => setTplAnchor(e.target.value)}
                  required
                  data-testid="tpl-anchor-input"
                />
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">
                  {selectedTemplate.anchor === "birth_date"
                    ? t("calendar.tpl_anchor_hint_birth") ||
                      "Events will be calculated relative to this date (e.g. vaccine at week 8)."
                    : t("calendar.tpl_anchor_hint_start") ||
                      "Events will start from this date."}
                </p>
              </div>

              <div>
                <Label>{t("calendar.field_pet") || "Pet"}</Label>
                <Select
                  value={tplPetId || "__none__"}
                  onValueChange={(v) => setTplPetId(v === "__none__" ? "" : v)}
                >
                  <SelectTrigger data-testid="tpl-pet-select">
                    <SelectValue
                      placeholder={t("calendar.no_pet") || "No specific pet"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">
                      {t("calendar.no_pet") || "No specific pet"}
                    </SelectItem>
                    {pets
                      .filter(
                        (p) =>
                          selectedTemplate.species === "both" ||
                          !p.species ||
                          p.species.toLowerCase() === selectedTemplate.species,
                      )
                      .map((p) => (
                        <SelectItem key={p.pet_id} value={p.pet_id}>
                          {p.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {selectedTemplate.species !== "both" &&
                  pets.length > 0 &&
                  !pets.some(
                    (p) =>
                      !p.species ||
                      p.species.toLowerCase() === selectedTemplate.species,
                  ) && (
                    <p className="text-[11px] text-[hsl(var(--terracotta))] mt-1">
                      {t("calendar.tpl_no_matching_pet") ||
                        `This template is designed for ${selectedTemplate.species}s.`}
                    </p>
                  )}
              </div>

              <div className="rounded-lg bg-[hsl(var(--muted))]/40 p-3 text-xs text-[hsl(var(--muted-foreground))]">
                <Bell className="h-3.5 w-3.5 inline mr-1" />
                {t("calendar.tpl_reminders_default") ||
                  "Reminders will be set to 24h email by default — you can adjust per event after."}
              </div>

              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedTemplate(null)}
                  disabled={applyingTpl}
                  data-testid="tpl-back-btn"
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  {t("calendar.tpl_back") || "Back"}
                </Button>
                <Button
                  type="button"
                  onClick={applyTemplate}
                  disabled={applyingTpl || !tplAnchor}
                  data-testid="tpl-apply-btn"
                  className="bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-white"
                >
                  {applyingTpl && (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  )}
                  <Sparkles className="mr-1 h-4 w-4" />
                  {(t("calendar.tpl_apply") || "Add {count} events").replace(
                    "{count}",
                    selectedTemplate.event_count,
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month grid
// ---------------------------------------------------------------------------
function MonthGrid({ cells, weekdayShort, viewDate, onPickDay, onClickEvent, onToggleComplete, t, lang }) {
  const today = startOfDay(new Date());
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
          {weekdayShort.map((w) => (
            <div key={w} className="px-2 py-2 text-center font-bold">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 auto-rows-fr">
          {cells.map(({ day, events: dayEvents }, idx) => {
            const inMonth = day.getMonth() === viewDate.getMonth();
            const isToday = isSameDay(day, today);
            return (
              <button
                type="button"
                key={idx}
                onClick={() => onPickDay(day)}
                data-testid={`calendar-cell-${day.toISOString().slice(0, 10)}`}
                className={
                  "group relative min-h-[92px] border-b border-r border-[hsl(var(--border))] p-1.5 text-left transition hover:bg-[hsl(var(--muted))]/30 " +
                  (inMonth ? "" : "bg-[hsl(var(--muted))]/20 text-[hsl(var(--muted-foreground))]/70 ") +
                  (isToday ? "bg-[hsl(var(--terracotta))]/8" : "")
                }
              >
                <div className="mb-1 flex items-center justify-between">
                  <span
                    className={
                      "inline-flex h-6 min-w-6 items-center justify-center rounded-full text-xs font-bold " +
                      (isToday
                        ? "bg-[hsl(var(--terracotta))] text-white"
                        : inMonth
                          ? "text-[hsl(var(--foreground))]"
                          : "text-[hsl(var(--muted-foreground))]")
                    }
                  >
                    {day.getDate()}
                  </span>
                  {dayEvents.length > 3 && (
                    <span className="text-[10px] font-bold text-[hsl(var(--muted-foreground))]">
                      +{dayEvents.length - 3}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-0.5">
                  {dayEvents.slice(0, 3).map((occ, i) => (
                    <span
                      key={`${occ.id}-${i}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickEvent(occ);
                      }}
                      className={
                        "inline-flex items-center gap-1 truncate rounded-md border px-1.5 py-0.5 text-[10px] font-semibold " +
                        (CATEGORY_COLORS[occ.category] || CATEGORY_COLORS.custom) +
                        (occ.is_completed ? " line-through opacity-60" : "")
                      }
                      title={occ.title}
                    >
                      <span>{occ.category_emoji}</span>
                      <span className="truncate">{occ.title}</span>
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Upcoming list
// ---------------------------------------------------------------------------
function UpcomingList({ events, onClickEvent, onToggleComplete, onAddEvent, t, lang, catLabel }) {
  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
          <CalendarDays className="h-10 w-10 text-[hsl(var(--muted-foreground))]" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            {t("calendar.empty_upcoming") || "No upcoming events. Add one to get started."}
          </p>
          <Button
            onClick={onAddEvent}
            className="gap-1.5 bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-white"
            data-testid="calendar-empty-add"
          >
            <Plus className="h-4 w-4" /> {t("calendar.add_event") || "Add event"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Group by date
  const groups = {};
  events.forEach((e) => {
    const k = new Date(e.start_at).toDateString();
    if (!groups[k]) groups[k] = [];
    groups[k].push(e);
  });

  return (
    <div className="space-y-4" data-testid="calendar-upcoming-list">
      {Object.entries(groups).map(([k, items]) => {
        const d = new Date(k);
        return (
          <Card key={k}>
            <CardContent className="p-4">
              <div className="mb-3 flex items-baseline justify-between">
                <h3 className="font-display text-base font-bold text-[hsl(var(--foreground))]">
                  {fmtDate(d, lang)}
                </h3>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">
                  {items.length} {items.length === 1 ? "event" : "events"}
                </span>
              </div>
              <ul className="space-y-2">
                {items.map((occ, i) => (
                  <li
                    key={`${occ.id}-${i}`}
                    className={
                      "flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-paper px-3 py-2.5 transition hover:border-[hsl(var(--terracotta))]/40 " +
                      (occ.is_completed ? "opacity-60" : "")
                    }
                  >
                    <button
                      type="button"
                      onClick={() => onToggleComplete(occ)}
                      disabled={occ.is_read_only}
                      data-testid={`upcoming-complete-${occ.id}`}
                      className={
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition " +
                        (occ.is_completed
                          ? "border-emerald-500 bg-emerald-500 text-white"
                          : "border-[hsl(var(--border))] hover:border-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/10")
                      }
                      aria-label="Mark complete"
                    >
                      {occ.is_completed ? <Check className="h-3.5 w-3.5" /> : null}
                    </button>

                    <button
                      type="button"
                      onClick={() => onClickEvent(occ)}
                      className="flex flex-1 items-center gap-3 text-left"
                    >
                      <span className="text-xl">{occ.category_emoji}</span>
                      <div className="flex-1 min-w-0">
                        <div
                          className={
                            "truncate text-sm font-semibold text-[hsl(var(--foreground))] " +
                            (occ.is_completed ? "line-through" : "")
                          }
                        >
                          {occ.title}
                          {occ.pet_name && (
                            <span className="ml-2 text-xs font-medium text-[hsl(var(--muted-foreground))]">
                              · {occ.pet_name}
                            </span>
                          )}
                          {occ.is_read_only && (
                            <Badge variant="secondary" className="ml-2 text-[10px]">
                              {t("calendar.auto") || "auto"}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">
                          {fmtTime(new Date(occ.start_at), lang)} · {catLabel(occ.category)}
                        </div>
                      </div>
                      {!occ.is_read_only && <Pencil className="h-3.5 w-3.5 text-[hsl(var(--muted-foreground))] opacity-0 group-hover:opacity-100" />}
                    </button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ChannelToggle({ label, checked, onChange, testId, hint }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <Checkbox
        checked={checked}
        onCheckedChange={(c) => onChange(!!c)}
        data-testid={testId}
      />
      <span className="text-sm">{label}</span>
      {hint && (
        <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
          {hint}
        </span>
      )}
    </label>
  );
}
